import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { HealthCardValidatorFactory } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

import styles from "./Identifiers.module.scss";

interface HealthCardsViewProps {
    healthCards: PatientIdentifier[],
    representant: LinkedContact
}

function HealthCardsView(props: HealthCardsViewProps): JSX.Element {
    const { getText } = useTranslation();

    const mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");

    return (
        <table>
            {props.healthCards && props.healthCards.length > 0 && props.healthCards.map((x, idx) => {
                // display healthcard as CURP if we are in mexico, NAM in QC, etc.
                const isMexicoCURP = x.CountryLookupId == mexicoCountryLookup.LookupID;
                const validator = HealthCardValidatorFactory.getValidator(x.CountryLookupId, x.ProvinceLookupId);
                const isSelectedProvinceOntario = x.ProvinceLookupId == cacheStore.FindLookupByLookupCode(CanadaState.Ontario).LookupID;

                const expirationDate = x.ExpiryDate
                    ? LuxonDateTimeParser.parse(x.ExpiryDate).toFormat("yyyy-MM-dd")
                    : "";

                return (
                    <tbody key={`hc${idx}`} className={styles.identifiers}>
                        <tr>
                            <td className="c1"><span className="form-label">{getText("CountryLabel")}</span></td>
                            <td className="c2"><strong data-cy="patientProfile-healthCard-country">{cacheStore.GetLocalizedValueByLookupId(x.CountryLookupId)}</strong></td>
                        </tr>
                        <tr>
                            <td className="c1"><span className="form-label">{getText("StateProvinceLabel")}</span></td>
                            <td className="c2"><strong data-cy="patientProfile-healthCard-state">{cacheStore.GetLocalizedValueByLookupId(x.ProvinceLookupId)}</strong></td>
                        </tr>
                        <tr>
                            <td className="c1"><span className="form-label">{validator.healthCardLabel}</span></td>
                            <td className="c2"><strong data-cy="patientProfile-healthCard-healthNumber">{x.Number}</strong></td>
                        </tr>
                        {!isMexicoCURP && (
                            <>
                                {!isSelectedProvinceOntario && (
                                    <tr>
                                        <td className="c1"><label className="form-label">{getText("HealthCardVersionLabel")}</label></td>
                                        <td className="c2"><strong data-cy="patientProfile-healthCard-version">{x.Version}</strong></td>
                                    </tr>

                                )}
                                <tr>
                                    <td className="c1"><span className="form-label">{getText("ExpiryLabel")}</span></td>
                                    <td className="c2"><strong data-cy="patientProfile-healthCard-expiry">{expirationDate}</strong></td>
                                </tr>
                            </>
                        )}
                    </tbody>
                );
            })}
            {(!props.healthCards || props.healthCards.length <= 0) && props.representant != null && (
                <tbody key="hc_rep" style={{ borderTop: "15px solid white" }}>
                    <tr>
                        <td className="c1"><span className="form-label">{getText("RepresentativeLabel")}</span></td>
                        <td className="c2"><strong>{cacheStore.GetLocalizedValueByLookupId(props.representant.RelationLookupId)}</strong></td>
                    </tr>
                    <tr>
                        <td className="c1"><span className="form-label">{sessionStore.getDefaultHealthCardLabel()}</span></td>
                        <td className="c2"><strong>{LinkedContact.GetNAM(props.representant)}</strong></td>
                    </tr>
                </tbody>
            )}
            {(!props.healthCards || props.healthCards.length <= 0) && props.representant == null && (
                <tbody key="hc">
                    <tr>
                        <td className="c1"><span className="form-label" /></td>
                        <td className="c2"><strong data-cy="patientProfile-healthCard">--</strong></td>
                    </tr>
                </tbody>
            )}
        </table>
    );
}

export { HealthCardsView };
